import {
  allowedIncomingTransitionsFor,
  normalizeIncomingTax,
  type CreateIncomingInvoiceInput,
  type IncomingInvoice,
  type IncomingInvoiceGateway,
  type IncomingInvoiceStatus,
  type IncomingTaxRate,
  type ListIncomingInput,
  type TransitionIncomingInput,
  type UpdateAccountingInput,
} from './types';

/** Offline incoming-invoice source. Per-tenant ER-26-NNNNNN capture numbers, validated workflow. */
function captureNumber(sequence: number): string {
  return `ER-26-${String(sequence).padStart(6, '0')}`;
}

function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

interface SeedInput {
  readonly tenantId: string;
  readonly seq: number;
  readonly status: IncomingInvoiceStatus;
  readonly supplierNumber: string;
  readonly supplierName: string;
  readonly supplierInvoiceNumber: string;
  readonly invoiceDate: string;
  readonly netAmount: number;
  readonly taxRate: IncomingTaxRate;
  readonly accountNumber?: string;
  readonly costCenter?: string;
  readonly bookingText?: string;
}

function seedInvoice(input: SeedInput): IncomingInvoice {
  const { taxAmount, grossAmount } = normalizeIncomingTax(input.netAmount, input.taxRate);
  return {
    id: `${input.tenantId}-incoming-${input.seq}`,
    tenantId: input.tenantId,
    number: captureNumber(input.seq),
    supplierNumber: input.supplierNumber,
    supplierName: input.supplierName,
    supplierInvoiceNumber: input.supplierInvoiceNumber,
    invoiceDate: input.invoiceDate,
    dueDate: addDays(input.invoiceDate, 30),
    netAmount: input.netAmount,
    taxRate: input.taxRate,
    taxAmount,
    grossAmount,
    status: input.status,
    accountNumber: input.accountNumber ?? '',
    costCenter: input.costCenter ?? '',
    bookingText: input.bookingText ?? '',
    reviewerRole: input.accountNumber ? 'betriebsleitung' : '',
    note: '',
    decisionNote: '',
    paidAt: input.status === 'paid' ? '2026-07-12' : null,
    createdAt: `${input.invoiceDate}T09:00:00.000Z`,
  };
}

/**
 * Supplier numbers sit in the creditor range (70000+), separate from the debitor range the
 * customers use — otherwise the DATEV stack shows one account number as both a Debitor and a
 * Kreditor for two different companies, which is exactly what the Fibu must not receive.
 */
const seeds: readonly IncomingInvoice[] = [
  seedInvoice({
    tenantId: 'ftw', seq: 1, status: 'paid', supplierNumber: '70002', supplierName: 'REMA Deutschland GmbH',
    supplierInvoiceNumber: 'RE-2026-8842', invoiceDate: '2026-06-28', netAmount: 1240, taxRate: 19,
    accountNumber: '3400', costCenter: 'Werkstatt', bookingText: 'Ersatzteile Hydraulik',
  }),
  seedInvoice({
    tenantId: 'ftw', seq: 2, status: 'review_requested', supplierNumber: '70001', supplierName: 'Hydraulik Nord GmbH',
    supplierInvoiceNumber: '2026-1177', invoiceDate: '2026-07-06', netAmount: 860, taxRate: 19,
    accountNumber: '3400', costCenter: 'Werkstatt', bookingText: 'Hydraulikschläuche',
  }),
  seedInvoice({
    tenantId: 'ftw', seq: 3, status: 'captured', supplierNumber: '70002', supplierName: 'REMA Deutschland GmbH',
    supplierInvoiceNumber: 'RE-2026-9013', invoiceDate: '2026-07-11', netAmount: 320.5, taxRate: 19,
  }),
  seedInvoice({
    tenantId: 'stw', seq: 1, status: 'payment_requested', supplierNumber: '70003', supplierName: 'Reifenservice West KG',
    supplierInvoiceNumber: 'R-88231', invoiceDate: '2026-07-05', netAmount: 2180, taxRate: 19,
    accountNumber: '3120', costCenter: 'Fuhrpark', bookingText: 'Reifen Schwerlast',
  }),
];

export class DemoIncomingInvoiceGateway implements IncomingInvoiceGateway {
  private readonly invoices: IncomingInvoice[] = seeds.map((invoice) => ({ ...invoice }));
  private readonly nextSequence: Record<string, number> = { ftw: 4, stw: 2, gsl: 1 };

  async listIncomingInvoices({ tenantId, status = 'all' }: ListIncomingInput): Promise<readonly IncomingInvoice[]> {
    return this.invoices
      .filter((invoice) => invoice.tenantId === tenantId)
      .filter((invoice) => status === 'all' || invoice.status === status)
      .slice()
      .sort((left, right) => right.number.localeCompare(left.number));
  }

  async createIncomingInvoice(input: CreateIncomingInvoiceInput): Promise<IncomingInvoice> {
    if (this.invoices.some((invoice) => invoice.tenantId === input.tenantId
      && invoice.supplierNumber === input.supplierNumber
      && invoice.supplierInvoiceNumber === input.supplierInvoiceNumber)) {
      throw new Error('Diese Lieferantenrechnung wurde für diesen Lieferanten bereits erfasst.');
    }
    if (!(input.netAmount > 0)) throw new Error('Der Nettobetrag muss größer als 0 sein.');
    const sequence = this.nextSequence[input.tenantId] ?? 1;
    this.nextSequence[input.tenantId] = sequence + 1;
    const { taxAmount, grossAmount } = normalizeIncomingTax(input.netAmount, input.taxRate);
    const invoice: IncomingInvoice = {
      id: `${input.tenantId}-incoming-${sequence}-${new Date().toISOString()}`,
      tenantId: input.tenantId,
      number: captureNumber(sequence),
      supplierNumber: input.supplierNumber,
      supplierName: input.supplierName,
      supplierInvoiceNumber: input.supplierInvoiceNumber,
      invoiceDate: input.invoiceDate,
      dueDate: input.dueDate ?? addDays(input.invoiceDate, 30),
      netAmount: input.netAmount,
      taxRate: input.taxRate,
      taxAmount,
      grossAmount,
      status: 'captured',
      accountNumber: '',
      costCenter: '',
      bookingText: '',
      reviewerRole: '',
      note: input.note ?? '',
      decisionNote: '',
      paidAt: null,
      createdAt: new Date().toISOString(),
    };
    this.invoices.unshift(invoice);
    return invoice;
  }

  async updateAccounting({ tenantId, id, accountNumber, costCenter, bookingText, reviewerRole }: UpdateAccountingInput): Promise<IncomingInvoice> {
    const index = this.find(tenantId, id);
    const current = this.invoices[index];
    const updated: IncomingInvoice = {
      ...current,
      accountNumber: accountNumber.trim(),
      costCenter: costCenter.trim(),
      bookingText: bookingText.trim(),
      reviewerRole,
      // Saving the Kontierung moves a freshly captured invoice to "vorkontiert" (legacy behaviour).
      status: current.status === 'captured' ? 'pre_booked' : current.status,
    };
    this.invoices[index] = updated;
    return updated;
  }

  async transitionIncoming({ tenantId, id, toStatus, note }: TransitionIncomingInput): Promise<IncomingInvoice> {
    const index = this.find(tenantId, id);
    const current = this.invoices[index];
    if (!allowedIncomingTransitionsFor(current.status).includes(toStatus)) {
      throw new Error(`Der Statuswechsel von ${current.status} zu ${toStatus} ist nicht zulässig.`);
    }
    if (toStatus === 'rejected' && !note?.trim()) {
      throw new Error('Eine Ablehnung benötigt eine Begründung.');
    }
    const updated: IncomingInvoice = {
      ...current,
      status: toStatus,
      decisionNote: note?.trim() ? note.trim() : current.decisionNote,
      paidAt: toStatus === 'paid' ? new Date().toISOString().slice(0, 10) : current.paidAt,
    };
    this.invoices[index] = updated;
    return updated;
  }

  private find(tenantId: string, id: string): number {
    const index = this.invoices.findIndex((invoice) => invoice.id === id && invoice.tenantId === tenantId);
    if (index < 0) throw new Error('Die Eingangsrechnung wurde in diesem Mandanten nicht gefunden.');
    return index;
  }
}

export function createDemoIncomingInvoiceGateway(): IncomingInvoiceGateway {
  return new DemoIncomingInvoiceGateway();
}
